import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Newspaper, ListChecks, TrendingUp, BookOpen, Users, ArrowRight, Quote, FileSpreadsheet, IdCard } from "lucide-react";
import GlassCard from "../../components/ui-custom/GlassCard";
import Reveal from "../../components/ui-custom/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const quickLinks = [
  { label: "My Sheet", to: "/employees/my-sheet", icon: FileSpreadsheet, blurb: "Add/edit your own deliverables and bookings." },
  { label: "Deliverables Dashboard", to: "/employees/deliverables", icon: ListChecks, blurb: "Who owns what, across the whole team." },
  { label: "Order Bookings", to: "/employees/orders", icon: TrendingUp, blurb: "Latest bookings across the team." },
  { label: "Stories", to: "/employees/stories", icon: BookOpen, blurb: "Vision, milestones and business updates." },
  { label: "Employee Directory", to: "/employees/directory", icon: Users, blurb: "Browse colleagues (complete your profile first)." },
];

const formatNewsDate = (pubDate) => {
  if (!pubDate) return "";
  const d = new Date(pubDate);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const EmployeesHome = () => {
  const [news, setNews] = useState(null);
  const [profileIncomplete, setProfileIncomplete] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/employees/news`, { withCredentials: true })
      .then((res) => setNews(res.data.items || []))
      .catch(() => setNews([]));

    axios
      .get(`${API}/employees/profile`, { withCredentials: true })
      .then((res) => setProfileIncomplete(!res.data.exists || !res.data.employee_code))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      {profileIncomplete && (
        <Reveal>
          <Link to="/employees/profile">
            <GlassCard className="p-5 flex items-center gap-4 border-qcyan/30">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25 shrink-0">
                <IdCard size={18} className="text-qcyan" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Complete your profile to unlock more</p>
                <p className="text-xs text-muted-foreground">
                  Add your Employee Code to access the Directory and get your personal Sheet set up.
                </p>
              </div>
              <ArrowRight size={16} className="text-qcyan shrink-0" />
            </GlassCard>
          </Link>
        </Reveal>
      )}

      <Reveal>
        <GlassCard accent="amber" className="p-7">
          <div className="flex items-start gap-4">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25 shrink-0">
              <Quote size={20} className="text-qcyan" />
            </span>
            <div>
              <p className="section-eyebrow mb-2">A Note From Our JMD</p>
              <p className="text-sm text-foreground leading-relaxed">
                "Every deliverable we ship and every order we book is part of a bigger story - powering the
                world's energy transition. Thank you for the work you put in every day. Explore the latest
                updates and stories from across Quality Power below."
              </p>
              <p className="text-xs text-muted-foreground mt-3">Bharanidharan Pandyan, Joint Managing Director</p>
            </div>
          </div>
        </GlassCard>
      </Reveal>

      <div className="grid sm:grid-cols-3 gap-5">
        {quickLinks.map((link, i) => (
          <Reveal key={link.to} delay={i * 0.06}>
            <Link to={link.to}>
              <GlassCard tilt className="p-6 h-full">
                <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25 mb-4">
                  <link.icon size={22} className="text-qcyan" />
                </span>
                <h3 className="font-display font-semibold text-foreground mb-1 flex items-center gap-1.5">
                  {link.label} <ArrowRight size={14} className="text-qcyan" />
                </h3>
                <p className="text-xs text-muted-foreground">{link.blurb}</p>
              </GlassCard>
            </Link>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <GlassCard className="p-7">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
              <Newspaper size={20} className="text-qcyan" />
            </span>
            <div>
              <h3 className="font-display font-semibold text-foreground text-lg">Latest News</h3>
              <p className="text-xs text-muted-foreground">Auto-updated coverage of Quality Power &amp; leadership</p>
            </div>
          </div>

          {news === null && <p className="text-sm text-muted-foreground">Loading latest headlines…</p>}
          {news !== null && news.length === 0 && (
            <p className="text-sm text-muted-foreground">No recent headlines found.</p>
          )}
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {news?.slice(0, 8).map((item) => (
              <li key={item.link} className="border-b border-border/50 pb-4 last:border-none last:pb-0">
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-foreground hover:text-qcyan transition-colors leading-snug"
                >
                  {item.title}
                </a>
                <p className="text-xs text-muted-foreground mt-1">
                  {item.source || "News"}
                  {item.pub_date ? ` · ${formatNewsDate(item.pub_date)}` : ""}
                </p>
              </li>
            ))}
          </ul>
        </GlassCard>
      </Reveal>
    </div>
  );
};

export default EmployeesHome;
